import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Container, Card, CardContent, CircularProgress, Button, Alert, Grid } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import API_BASE from '../apiBase';
import useConfirmDialog from '../hooks/useConfirmDialog';
import ConfirmDialog from '../components/pr/ConfirmDialog';
import PRHeader from '../components/pr/PRHeader';
import PRToolbar from '../components/pr/PRToolbar';
import FileTree from '../components/pr/FileTree/FileTree';
import FileTreeBadges from '../components/pr/FileTree/FileTreeBadges';
import PRCommentsDialog from '../components/pr/Comments/PRCommentsDialog';
import FixDialog from '../components/pr/Fix/FixDialog';
import useStagedFixes from '../hooks/useStagedFixes';
import useFileDiffs from '../hooks/useFileDiffs';
import usePRFileTree from '../hooks/usePRFileTree';
import FileDiffPanel from '../components/pr/DiffViewer/FileDiffPanel';
import useSelectedFileBasePrefetch from '../hooks/useSelectedFileBasePrefetch';
import useFixWorkflow from '../hooks/useFixWorkflow';
import usePRDetails from '../hooks/usePRDetails';
import { buildAfterFixLineSet, buildIntentionalLineSet } from '../components/pr/DiffViewer/helpers';

function BitbucketPRDetails() {
  const params = useParams();
  const { ownerAndRepo, prNumber } = params;
  const owner = params.owner || params.workspace || params.organization;
  const repo = params.repo || params.repoSlug || params.project;
  const [orgFinal, projFinal] = ownerAndRepo
    ? decodeURIComponent(ownerAndRepo).split('/')
    : [owner, repo];
  const navigate = useNavigate();
  const location = useLocation();

  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);
  const [fixedFile, setFixedFile] = useState(null);
  const [fixToken, setFixToken] = useState(null);
  const [pendingFixMeta, setPendingFixMeta] = useState(null);
  const [prCommentsOpen, setPrCommentsOpen] = useState(false);
  const { confirmState, openConfirm, closeConfirm } = useConfirmDialog();
  const [commitLoading, setCommitLoading] = useState(false);
  const fixingFilePathRef = useRef('');
  const fixControllersRef = useRef(new Set());

  const getProjectKey = () => {
    const qp = new URLSearchParams(location.search);
    const fromUrl = qp.get('project_key');
    if (fromUrl) { sessionStorage.setItem('qdex:lastProjectKey', fromUrl); return fromUrl; }
    return sessionStorage.getItem('qdex:lastProjectKey') || '';
  };

  const storageKey = `qdex:stagedFixes:bitbucket:${orgFinal}/${projFinal}/${prNumber}`;
  const { appliedFixes, setAppliedFixes, appliedTokens, setAppliedTokens, intentionalStaged, setIntentionalStaged, persistStaged, rehydrateStaged } = useStagedFixes(storageKey, { ttlMs: 30 * 60 * 1000 });

  const pageScrollRef = useRef(null);
  const contentScrollRef = useRef(null);
  const contentScrollRightRef = useRef(null);
  const [scrolledIntoPanes, setScrolledIntoPanes] = useState(false);
  const appBarHeight = 56;
  const prBarHeight = 48;
  const paneHeight = `calc(100vh - ${appBarHeight + prBarHeight}px)`;

  const { open: sidebarOpen, drawerWidth: sidebarWidth } = React.useContext(require('../components/SidebarContext').SidebarContext);
  const effectiveWidth = sidebarOpen ? sidebarWidth : 0;

  const { fileCache, setFileCache, loadingFileMap, setLoadingFileMap, ensureFileLoaded, abortAll } = useFileDiffs({ provider: 'bitbucket', identifiers: { organization: orgFinal, project: projFinal, repositoryID: '', prNumber }, getProjectKey });

  const { prDetails, setPrDetails, loading, error, setError, fileTree, expandedNodes, setExpandedNodes, selectedFilePath, setSelectedFilePath, fetchPRDetails, normalizePath } = usePRDetails({ provider: 'bitbucket', identifiers: { organization: orgFinal, project: projFinal, repositoryID: '', prNumber }, getProjectKey, fileCache });

  const { buildFileTree, getAllFilePathsFromTree } = usePRFileTree();
  const toggleDir = (path) => setExpandedNodes((prev) => ({ ...prev, [path]: !prev[path] }));

  useEffect(() => {
    const sel = normalizePath(selectedFilePath || '');
    if (!sel) return;
    const file = (prDetails?.files || []).find(f => normalizePath(f.path || f.filename || '') === sel);
    const hasDiff = !!(file && Array.isArray(file.diff_lines) && file.diff_lines.length > 0);
    if (!hasDiff) { ensureFileLoaded(sel); }
  }, [selectedFilePath, prDetails]);

  const { postedBasePathsRef, prefetchStartedRef } = useSelectedFileBasePrefetch({ API_BASE, provider: 'bitbucket', organization: orgFinal, project: projFinal, repositoryID: '', prNumber, getProjectKey, prDetails, selectedFilePath, normalizePath, fileTree, getAllFilePathsFromTree, loadingFileMap, fileCache, ensureFileLoaded });

  // Abort background prefetch immediately if the route changes
  useEffect(() => {
    return () => {
      try { abortAll(); } catch {}
      try { setLoadingFileMap({}); } catch {}
      try { if (prefetchStartedRef && prefetchStartedRef.current) prefetchStartedRef.current = false; } catch {}
      try { if (postedBasePathsRef && postedBasePathsRef.current) postedBasePathsRef.current.clear(); } catch {}
    };
  }, [location.pathname]);

  const { handleFixRequest, handleMarkIntentional, handleFixFileRequest, handleCommitFix, handleDiscardFix, handleCommitAll, handleDiscardAll, handleDiscardApplied, handleViewAppliedFix } = useFixWorkflow({
    provider: 'bitbucket',
    API_BASE,
    identifiers: { organization: orgFinal, project: projFinal, repositoryID: '', prNumber },
    getProjectKey,
    normalizePath,
    refs: { fixControllersRef },
    state: { appliedFixes, setAppliedFixes, appliedTokens, setAppliedTokens, intentionalStaged, setIntentionalStaged, setError, setLoadingFileMap, setFileCache, setPrDetails },
    ui: { setFixDialogOpen, setFixLoading, setFixedFile, setFixToken, setPendingFixMeta },
    storageKey,
  });

  useEffect(() => { rehydrateStaged(); }, [orgFinal, projFinal, prNumber]);
  useEffect(() => {
    const left = contentScrollRef.current; const right = contentScrollRightRef.current; const page = pageScrollRef.current;
    const onScrollAny = () => {
      const yLeft = left ? left.scrollTop || 0 : 0; const yRight = right ? right.scrollTop || 0 : 0; const yPage = page ? page.scrollTop || 0 : 0;
      const shouldStick = yPage > 0 || ((yLeft > 0 || yRight > 0) && yPage > 0);
      setScrolledIntoPanes(shouldStick);
    };
    left && left.addEventListener('scroll', onScrollAny, { passive: true }); right && right.addEventListener('scroll', onScrollAny, { passive: true }); page && page.addEventListener('scroll', onScrollAny, { passive: true });
    return () => { left && left.removeEventListener('scroll', onScrollAny); right && right.removeEventListener('scroll', onScrollAny); page && page.removeEventListener('scroll', onScrollAny); };
  }, []);

  const isPRActive = ((prDetails?.status || '').toString().toLowerCase() === 'active');

  if (loading) return (<Box sx={{ backgroundColor: '#0d1117', minHeight: '100vh', color: '#f0f6fc', m: 0, p: 0, position: 'fixed', top: '56px', left: 0, right: 0, bottom: 0, zIndex: 1000, overflow: 'auto' }}><Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress sx={{ color: '#58a6ff' }} /></Box></Box>);
  if (error) return (
    <Box sx={{ backgroundColor: '#0d1117', minHeight: '100vh', color: '#f0f6fc', m: 0, p: 0, position: 'fixed', top: '56px', left: 0, right: 0, bottom: 0, zIndex: 1000, overflow: 'auto' }}>
      <Box>
        <Container maxWidth={false} sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mt: 4, backgroundColor: '#67060c', color: '#f85149', border: '1px solid #f85149', whiteSpace: 'pre-line' }}>{error}</Alert>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/pull-requests')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Back to List</Button>
            <Button onClick={() => navigate('/app/projects')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Open Projects to Update Token</Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
  if (!prDetails) return (
    <Box sx={{ backgroundColor: '#0d1117', minHeight: '100vh', color: '#f0f6fc', m: 0, p: 0, position: 'fixed', top: '56px', left: 0, right: 0, bottom: 0, zIndex: 1000, overflow: 'auto' }}>
      <Box>
        <Container maxWidth={false} sx={{ mt: 4 }}>
          <Alert severity="info" sx={{ mt: 4, backgroundColor: '#0c532d', color: '#7ee787', border: '1px solid #3fb950' }}>No PR details available.</Alert>
          <Box sx={{ mt: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/pull-requests')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Back to List</Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );

  const commonStyles = { backgroundColor: '#0d1117', minHeight: '100vh', color: '#f0f6fc', m: 0, p: 0, position: 'fixed', top: `${appBarHeight}px`, left: 0, right: 0, bottom: 0, zIndex: 1000, overflow: 'auto' };
  const contentWrapperStyles = { ml: { xs: 0, md: `${effectiveWidth}px` }, width: { xs: '100%', md: `calc(100% - ${effectiveWidth}px)` }, display: 'block' };
  const containerStyles = { mt: 0, mb: 1, backgroundColor: '#0d1117', px: 2, pt: 0.5, pb: 1.5, m: 0, maxWidth: 'none', minHeight: '100vh', display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box' };

  return (
    <Box ref={pageScrollRef} sx={{ ...commonStyles }}>
      <Box sx={{ ...contentWrapperStyles }}>
        <Container maxWidth={false} sx={{ ...containerStyles }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/pull-requests')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Back to List</Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#f0f6fc' }}>PR #{prNumber}</Typography>
          </Box>

          <PRHeader
            title={prDetails.title}
            status={prDetails.status}
            repositoryLabel={`${orgFinal}/${projFinal}`}
            author={prDetails.author}
            createdLabel={prDetails.created_at ? format(new Date(prDetails.created_at), 'PPP p') : 'Unknown'}
            filesCount={prDetails.files?.length || 0}
            commentsCount={prDetails.total_comments || 0}
            onViewPRComments={() => setPrCommentsOpen(true)}
          />

          {(() => {
            const issuesCount = (prDetails?.comments || []).filter(c => !!c.file).length;
            const fixedCount = (prDetails?.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('fixed - ')).length;
            const intentionalCount = (prDetails?.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('marked intentional - ')).length;
            const stagedIntent = Object.values(intentionalStaged || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
            const stagedCount = appliedTokens.length + stagedIntent;
            const commitDisabled = (((appliedTokens.length === 0) && (stagedIntent === 0)) || commitLoading);
            const layout = scrolledIntoPanes ? { top: appBarHeight, left: (typeof effectiveWidth === 'number' ? `${effectiveWidth}px` : 0), width: { xs: '100%', md: `calc(100% - ${effectiveWidth}px)` }, right: 0 } : { };
            return (
              <PRToolbar
                issuesCount={issuesCount}
                fixedCount={fixedCount}
                intentionalCount={intentionalCount}
                stagedCount={stagedCount}
                onDiscardAll={() => openConfirm('Discard changes?', 'Are you sure you want to discard all staged fixes and intentional marks?', () => { closeConfirm(); handleDiscardAll(); })}
                onCommitAll={() => openConfirm('Commit changes?', 'Are you sure you want to commit all staged fixes and intentional marks?', async () => { setCommitLoading(true); try { await handleCommitAll(); } finally { setCommitLoading(false); } closeConfirm(); })}
                commitDisabled={commitDisabled}
                committing={commitLoading}
                height={prBarHeight}
                sticked={scrolledIntoPanes}
                layout={layout}
              />
            );
          })()}
          {scrolledIntoPanes && <Box sx={{ height: prBarHeight }} />}

          <Card sx={{ backgroundColor: '#161b22', border: '1px solid #30363d', borderRadius: '6px', flex: 1, position: 'sticky', top: appBarHeight + prBarHeight, zIndex: 1 }}>
            <CardContent sx={{ p: 0 }}>
              <Grid container>
                <Grid item xs={12} md={4} lg={3} sx={{ borderRight: '1px solid #30363d' }}>
                  <Box ref={contentScrollRef} sx={{ backgroundColor: '#0d1117', height: scrolledIntoPanes ? `calc(100vh - ${appBarHeight + prBarHeight}px)` : paneHeight, overflowY: 'auto', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { backgroundColor: '#0d1117' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#30363d', borderRadius: '8px', border: '2px solid #0d1117' }, scrollbarWidth: 'thin', scrollbarColor: '#30363d #0d1117' }}>
                    <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#0d1117', borderBottom: '1px solid #30363d', px: 2, py: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ color: '#7d8590', fontWeight: 600 }}>Files changed</Typography>
                    </Box>
                    <Box sx={{ px: 1.5, pt: 1 }}>
                      <FileTree
                        fileTree={fileTree}
                        expandedNodes={expandedNodes}
                        selectedFilePath={selectedFilePath}
                        normalizePath={normalizePath}
                        onToggleDir={(p) => toggleDir(p)}
                        onSelectFile={(p) => setSelectedFilePath(p)}
                        badgesForPath={(childKey) => {
                          const latestFile = (prDetails.files || []).find(f => normalizePath(f.path || f.filename || '') === childKey);
                          return <FileTreeBadges latestFile={latestFile} appliedFixes={appliedFixes} intentionalStaged={intentionalStaged} />;
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8} lg={9}>
                  <Box ref={contentScrollRightRef} sx={{ height: scrolledIntoPanes ? `calc(100vh - ${appBarHeight + prBarHeight}px)` : paneHeight, overflowY: 'auto', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { backgroundColor: '#0d1117' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#30363d', borderRadius: '8px', border: '2px solid #0d1117' }, scrollbarWidth: 'thin', scrollbarColor: '#30363d #0d1117' }}>
                    {(() => {
                      const baseFile = (prDetails.files || []).find(f => {
                        const fp = normalizePath(f.path || f.filename || '');
                        const sel = normalizePath(selectedFilePath);
                        if (fp === sel) return true; const fpName = fp.split('/').pop(); return fpName === sel || sel.split('/').pop() === fpName;
                      });
                      if (!baseFile) return <Typography sx={{ color: '#7d8590' }}>Select a file to view changes.</Typography>;
                      const currentPath = normalizePath(baseFile.path || baseFile.filename || '');
                      const cached = fileCache[currentPath];
                      const fileForView = cached ? { ...baseFile, ...cached } : baseFile;
                      if (Array.isArray(fileForView.diff_lines) && fileForView.diff_lines.length > 0) {
                        return (
                          <FileDiffPanel
                            file={fileForView}
                            isPRActive={isPRActive}
                            appliedFixes={appliedFixes}
                            intentionalStaged={intentionalStaged}
                            onFixFile={handleFixFileRequest}
                            onDiscardApplied={handleDiscardApplied}
                            onViewAppliedFix={handleViewAppliedFix}
                            fixingFilePath={fixingFilePathRef.current}
                            onFixRequest={handleFixRequest}
                            onMarkIntentional={handleMarkIntentional}
                            provider={'bitbucket'}
                            buildAfterFixLineSet={buildAfterFixLineSet}
                            buildIntentionalLineSet={buildIntentionalLineSet}
                            selectedFilePath={selectedFilePath}
                          />
                        );
                      }
                      const isLoading = !!loadingFileMap[currentPath];
                      return isLoading ? (
                            <Box sx={{ p: 6, textAlign: 'center', color: '#7d8590', backgroundColor: '#0d1117' }}>
                              <CircularProgress size={20} sx={{ color: '#58a6ff', mr: 1 }} />
                              <Typography variant="body2" component="span" sx={{ color: '#7d8590' }}>Loading file diff…</Typography>
                            </Box>
                      ) : (
                        <Box sx={{ p: 6, textAlign: 'center', color: '#7d8590', backgroundColor: '#0d1117' }}>
                          <Typography variant="body2" component="span" sx={{ color: '#7d8590' }}>This file was deleted.</Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <PRCommentsDialog open={prCommentsOpen} onClose={() => setPrCommentsOpen(false)} comments={prDetails?.comments || []} />

          <ConfirmDialog open={confirmState.open} title={confirmState.title} message={confirmState.message} onClose={closeConfirm} onConfirm={() => { if (typeof confirmState.onConfirm === 'function') confirmState.onConfirm(); }} loading={commitLoading} />

          <FixDialog open={fixDialogOpen} loading={fixLoading} fixedFile={fixedFile} fixToken={fixToken} isPRActive={isPRActive} onDiscard={() => handleDiscardFix({ fixToken, fixedFile })} onApply={() => handleCommitFix({ fixedFile, fixToken, pendingFixMeta })} onClose={() => { setFixLoading(false); setFixDialogOpen(false); }} />
        </Container>
      </Box>
    </Box>
  );
}

export default BitbucketPRDetails;



